"use client";

import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

import { cn } from "@/lib/utils";

export type SectionPanelProps = {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Optional icon rendered inline with the title. */
  icon?: LucideIcon;
  trailing?: React.ReactNode;
  children?: React.ReactNode;
  /** When true, skips the outer border so the panel nests inside another card. */
  embedded?: boolean;
  /** When true, keep embedded layout behavior but render a full border box. */
  forceFullBorder?: boolean;
  /** When true, pulls the panel out on mobile to span edge-to-edge (-mx-4 sm:mx-0). */
  bleedOnMobile?: boolean;
  /** When true, renders a chevron that toggles the children open/closed. No-op when there are no children. */
  collapsible?: boolean;
  /** Initial open state when `collapsible` is true. Defaults to true. */
  defaultOpen?: boolean;
  className?: string;
  /** Extra classes for the bordered card box (e.g. flex-fill so content can clamp to height). */
  cardClassName?: string;
  headerClassName?: string;
  contentClassName?: string;
};

export function SectionPanel({
  title,
  description,
  icon: Icon,
  trailing,
  children,
  embedded = false,
  forceFullBorder = false,
  bleedOnMobile = true,
  collapsible = false,
  defaultOpen = true,
  className,
  cardClassName,
  headerClassName,
  contentClassName,
}: SectionPanelProps) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  const canCollapse = collapsible && Boolean(children);
  const showChildren = !canCollapse || isOpen;

  const wrapperClasses = cn(
    bleedOnMobile && !embedded && "-mx-4 sm:mx-0",
    className,
  );

  const cardClasses = cn(
    embedded && !forceFullBorder
      ? "border-t border-border"
      : "border border-border",
    "bg-card",
    cardClassName,
  );

  const chevron = canCollapse ? (
    <button
      type="button"
      onClick={() => setIsOpen((prev) => !prev)}
      aria-expanded={isOpen}
      aria-label={isOpen ? "Collapse section" : "Expand section"}
      className="text-muted-foreground transition-colors hover:text-foreground"
    >
      <ChevronDownIcon
        className={cn(
          "h-4 w-4 transition-transform duration-150",
          !isOpen && "-rotate-90",
        )}
      />
    </button>
  ) : null;

  return (
    <section className={wrapperClasses}>
      <div className={cardClasses}>
        <div
          className={cn(
            "flex items-start justify-between gap-3 px-3 py-2.5 sm:px-4",
            headerClassName,
          )}
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              {Icon ? (
                <Icon
                  className="h-3.5 w-3.5 shrink-0 text-[var(--burgundy-core)]"
                  aria-hidden="true"
                />
              ) : null}
              <p className="text-xs font-extrabold uppercase tracking-[0.1em] text-[var(--burgundy-core)]">
                {title}
              </p>
            </div>
            {description ? (
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {description}
              </p>
            ) : null}
          </div>
          {trailing || chevron ? (
            <div className="flex shrink-0 items-center gap-2 pt-0.5">
              {trailing}
              {chevron}
            </div>
          ) : null}
        </div>
        {children && showChildren ? (
          <div
            className={cn(
              "border-t border-border",
              contentClassName,
            )}
          >
            {children}
          </div>
        ) : null}
      </div>
    </section>
  );
}
